/**
 * Mock WebSocket Bridge
 * Simulates real-time WebSocket communication for development
 * Provides instant escalation notifications to admin dashboard
 * Can be replaced with real WebSocket when backend server is ready
 * Production WebSocket example: wss://hygeawellnessspa.com/websocket
 */

import {
  realtimeEvents,
  EVENT_TYPES,
  emitEscalation,
  emitMessage,
} from './realtimeEventSystem';
import { WS_BASE } from '../config/runtime';

class MockWebSocketBridge {
  constructor() {
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;
    this.subscriptions = new Map();
    this.messageBuffer = [];
    this.isSimulating = false;
  }

  /**
   * Connect to websocket (simulated)
   * @param {string} url - WebSocket URL
   * @returns {Promise}
   */
  connect(url = WS_BASE) {
    return new Promise((resolve) => {
      console.log('[MockWebSocket] Connecting to:', url);

      // Simulate connection delay
      setTimeout(() => {
        this.connected = true;
        this.reconnectAttempts = 0;

        console.log('[MockWebSocket] Connected successfully');
        realtimeEvents.emit(EVENT_TYPES.CONNECTION_STATUS, {
          status: 'connected',
          timestamp: new Date().toISOString(),
        });

        // Send any buffered messages
        this.flushMessageBuffer();

        resolve();
      }, 300); // Simulate 300ms connection time
    });
  }

  /**
   * Disconnect from websocket
   */
  disconnect() {
    this.connected = false;
    console.log('[MockWebSocket] Disconnected');
    realtimeEvents.emit(EVENT_TYPES.CONNECTION_STATUS, {
      status: 'disconnected',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Subscribe to real-time updates for a ticket
   * @param {number} ticketId - Ticket ID to subscribe to
   * @param {Function} callback - Callback for updates
   * @returns {Function} Unsubscribe function
   */
  subscribe(ticketId, callback) {
    const subscriptionId = `sub-${ticketId}-${Date.now()}`;

    if (!this.subscriptions.has(ticketId)) {
      this.subscriptions.set(ticketId, []);
    }

    this.subscriptions.get(ticketId).push({
      id: subscriptionId,
      callback,
    });

    console.log(`[MockWebSocket] Subscribed to ticket ${ticketId}`);

    // Return unsubscribe function
    return () => {
      const subs = this.subscriptions.get(ticketId);
      if (subs) {
        const index = subs.findIndex((s) => s.id === subscriptionId);
        if (index > -1) {
          subs.splice(index, 1);
        }
      }
      console.log(`[MockWebSocket] Unsubscribed from ticket ${ticketId}`);
    };
  }

  /**
   * Send message through websocket
   * @param {Object} message - Message object
   * @returns {Promise}
   */
  send(message) {
    return new Promise((resolve, reject) => {
      if (!this.connected) {
        console.warn('[MockWebSocket] Not connected, buffering message');
        this.messageBuffer.push({ message, resolve, reject });
        return;
      }

      // Simulate send delay
      setTimeout(() => {
        console.log('[MockWebSocket] Message sent:', message);

        // Simulate acknowledgment
        resolve({
          success: true,
          messageId: message.id || `msg-${Date.now()}`,
          timestamp: new Date().toISOString(),
        });

        // Broadcast message to subscribers
        this.broadcastToSubscribers(message);
      }, 100);
    });
  }

  /**
   * Broadcast message to relevant subscribers
   * @private
   */
  broadcastToSubscribers(message) {
    const ticketId = message.ticketId || message.ticket_id;
    if (!ticketId) return;

    const subscribers = this.subscriptions.get(ticketId);
    if (subscribers && subscribers.length > 0) {
      subscribers.forEach((sub) => {
        try {
          sub.callback(message);
        } catch (error) {
          console.error('[MockWebSocket] Error in subscriber callback:', error);
        }
      });
    }
  }

  /**
   * Simulate incoming escalation from chatbot
   * Used for testing and development
   * @param {Object} escalationData - Escalation data
   */
  simulateEscalation(escalationData) {
    console.log('[MockWebSocket] Simulating escalation:', escalationData);

    // Emit event
    emitEscalation(escalationData);

    // Broadcast via real-time events
    setTimeout(() => {
      realtimeEvents.emit(EVENT_TYPES.ESCALATION_CONFIRMED, {
        ...escalationData,
        confirmedAt: new Date().toISOString(),
      });
    }, 500);

    // Broadcast to subscribers
    this.broadcastToSubscribers(escalationData);
  }

  /**
   * Simulate incoming message from admin
   * Used for testing and development
   * @param {Object} messageData - Message data
   */
  simulateAdminMessage(messageData) {
    console.log('[MockWebSocket] Simulating admin message:', messageData);

    // Add small delay for realism
    setTimeout(() => {
      emitMessage(messageData);
      this.broadcastToSubscribers(messageData);
    }, 200);
  }

  /**
   * Simulate incoming message from client
   * @param {Object} messageData - Message data
   */
  simulateClientMessage(messageData) {
    console.log('[MockWebSocket] Simulating client message:', messageData);

    setTimeout(() => {
      emitMessage(messageData);
      this.broadcastToSubscribers(messageData);
    }, 150);
  }

  /**
   * Flush buffered messages
   * @private
   */
  flushMessageBuffer() {
    while (this.messageBuffer.length > 0) {
      const { message, resolve } = this.messageBuffer.shift();
      this.send(message).then(resolve);
    }
  }

  /**
   * Get connection status
   * @returns {boolean} True if connected
   */
  isConnected() {
    return this.connected;
  }

  /**
   * Get subscription count for ticket
   * @param {number} ticketId - Ticket ID
   * @returns {number} Number of subscribers
   */
  getSubscriptionCount(ticketId) {
    const subs = this.subscriptions.get(ticketId);
    return subs ? subs.length : 0;
  }

  /**
   * Get all active subscriptions
   * @returns {Object} Map of ticket IDs to subscriber counts
   */
  getActiveSubscriptions() {
    const active = {};
    this.subscriptions.forEach((subs, ticketId) => {
      if (subs.length > 0) {
        active[ticketId] = subs.length;
      }
    });
    return active;
  }

  /**
   * Simulate network delay
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise}
   */
  simulateNetworkDelay(ms = 500) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Simulate network error
   * @returns {Promise} Rejected promise
   */
  simulateNetworkError() {
    return Promise.reject(new Error('Network error simulated'));
  }

  /**
   * Start simulating real-time activity (for testing)
   * @param {number} interval - Interval in ms between simulated events
   */
  startSimulatingActivity(interval = 5000) {
    if (this.isSimulating) return;

    this.isSimulating = true;
    console.log('[MockWebSocket] Starting simulation of real-time activity');

    this.simulationInterval = setInterval(() => {
      // Randomly simulate different events
      const random = Math.random();

      if (random < 0.3) {
        // Simulate typing indicator
        realtimeEvents.emit(EVENT_TYPES.ADMIN_TYPING, {
          ticketId: Math.floor(Math.random() * 5) + 1,
          adminName: 'Admin Support',
          timestamp: new Date().toISOString(),
        });
      } else if (random < 0.6) {
        // Simulate message
        realtimeEvents.emit(EVENT_TYPES.MESSAGE_RECEIVED, {
          ticketId: Math.floor(Math.random() * 5) + 1,
          message: 'Simulated message',
          timestamp: new Date().toISOString(),
        });
      } else {
        // Simulate status update
        realtimeEvents.emit(EVENT_TYPES.TICKET_UPDATED, {
          ticketId: Math.floor(Math.random() * 5) + 1,
          status: 'active',
          timestamp: new Date().toISOString(),
        });
      }
    }, interval);
  }

  /**
   * Stop simulating activity
   */
  stopSimulatingActivity() {
    if (this.simulationInterval) {
      clearInterval(this.simulationInterval);
      this.isSimulating = false;
      console.log('[MockWebSocket] Stopped simulation');
    }
  }

  /**
   * Get connection diagnostics
   * @returns {Object} Diagnostic information
   */
  getDiagnostics() {
    return {
      connected: this.connected,
      reconnectAttempts: this.reconnectAttempts,
      bufferedMessages: this.messageBuffer.length,
      activeSubscriptions: this.getActiveSubscriptions(),
      subscriptionCount: this.subscriptions.size,
      isSimulating: this.isSimulating,
    };
  }

  /**
   * Reset connection
   */
  reset() {
    this.disconnect();
    this.messageBuffer = [];
    this.subscriptions.clear();
    this.reconnectAttempts = 0;
    console.log('[MockWebSocket] Reset complete');
  }
}

// Create singleton instance
export const mockWebSocket = new MockWebSocketBridge();

/**
 * Initialize mock WebSocket bridge
 * @returns {Promise}
 */
export const initializeMockWebSocket = async () => {
  try {
    await mockWebSocket.connect();
    console.log('[MockWebSocket] Initialized successfully');
    return mockWebSocket;
  } catch (error) {
    console.error('[MockWebSocket] Initialization failed:', error);
    throw error;
  }
};

/**
 * Helper to broadcast escalation immediately
 * @param {Object} escalationData - Escalation data
 */
export const broadcastEscalationInstantly = (escalationData) => {
  mockWebSocket.simulateEscalation({
    ...escalationData,
    broadcastedAt: new Date().toISOString(),
  });
};

/**
 * Helper to broadcast message instantly
 * @param {Object} messageData - Message data
 * @param {string} sender - Sender type ('admin' or 'client')
 */
export const broadcastMessageInstantly = (messageData, sender = 'admin') => {
  if (sender === 'admin') {
    mockWebSocket.simulateAdminMessage(messageData);
  } else {
    mockWebSocket.simulateClientMessage(messageData);
  }
};

export default mockWebSocket;
